import React, { Children } from "react"
import { createStyles, View, forwardRef } from "@design-system/core"
import { PreviousNextButton } from "./PreviousNextButton"
import { TabIndicator } from "./TabIndicator"
import { TabListProps } from "./types"
import { useTabList } from "./useTabList"
import { useTabsContext } from "./TabsContext"

export const TabList = forwardRef<TabListProps, "div">(
  (props, forwardedRef) => {
    const { id, selectedIndex, showPreviousNextButtons } = useTabsContext()
    const {
      accessibilityLabel,
      style,
      children,
      swipeableHeight,
      hidePrevButton,
      hideNextButton,
      listRef,
      childLayouts,
      setTabContainerRef,
      goToPreviousPage,
      goToNextPage,
      handlePrevButtonLayout,
      handleNextButtonLayout,
      handleSwipeableLayout,
      getHandleItemClick,
      handleKeyDown,
      updateChildLayout,
      getHandleChildLayout,
      ...others
    } = useTabList(props)

    return (
      <View
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="navigation"
        onKeyDown={handleKeyDown}
        style={[styles.root, style]}
        ref={forwardedRef}
      >
        <View style={styles.swipeableContainer}>
          {showPreviousNextButtons && (
            <PreviousNextButton
              isNext={false}
              height={swipeableHeight}
              isDisabled={hidePrevButton}
              onClick={goToPreviousPage}
              onButtonLayout={handlePrevButtonLayout}
            />
          )}
          <View
            accessibilityRole="tablist"
            ref={listRef}
            style={styles.list}
            onLayout={handleSwipeableLayout}
          >
            <View style={styles.tabsContainer}>
              {Children.map(children, (tab, i) => (
                <View
                  accessibilityRole="presentation"
                  tabIndex={0}
                  key={`tab-container-${i}`}
                  onClick={getHandleItemClick(i)}
                  onLayout={getHandleChildLayout(i)}
                  style={[styles.item, i > 0 && styles.itemSpacing]}
                  ref={setTabContainerRef(i)}
                  nativeID={id ? `${id}--tab-${i}` : undefined}
                  aria-selected={selectedIndex === i}
                  aria-controls={id ? `${id}--tabpanel-${i}` : undefined}
                >
                  {React.isValidElement(tab) &&
                    React.cloneElement(tab, { index: i })}
                </View>
              ))}
            </View>
            <TabIndicator tabLayout={childLayouts.get(selectedIndex)} />
          </View>
          {showPreviousNextButtons && (
            <PreviousNextButton
              isNext
              height={swipeableHeight}
              isDisabled={hideNextButton}
              onClick={goToNextPage}
              onButtonLayout={handleNextButtonLayout}
            />
          )}
        </View>
      </View>
    )
  },
)

TabList.displayName = "TabList"

const styles = createStyles((theme) => ({
  root: {
    alignItems: "center",
    flexDirection: "row",
    overflow: "hidden",
    zIndex: 0,
  },
  swipeableContainer: {
    alignItems: "center",
    flexGrow: 1,
    flexBasis: "0%",
    flexDirection: "row",
    height: "100%",
  },
  list: {
    flexGrow: 1,
    flexDirection: "row",
    flexShrink: 1,
    flexWrap: "nowrap",
    height: "100%",
    zIndex: 0,
    scrollSnapType: "x mandatory",
    scrollbarWidth: "none",
    overflowX: "auto",
    // overflowY: "hidden",
  },
  tabsContainer: {
    flexDirection: "row",
    flexWrap: "nowrap",
    height: "100%",
    minWidth: "100%",
    borderBottomWidth: theme.borderWidths.small,
    borderBottomStyle: "solid",
    borderBottomColor: theme.borderColors.divider,
  },
  item: {
    paddingHorizontal: "0",
    scrollSnapAlign: "start",
  },
  itemSpacing: {
    marginLeft: theme.spaces.spacing16,
  },
}))
